import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { NonBreaking } from "@faire/web/common/string";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Ref } from "react";
import Slider from "react-slick";
import styled from "styled-components";

// eslint-disable-next-line import/no-unassigned-import
import "@faire/retailer/components/slick.css";

export const getTabithaEveImageUrl = (isSSR: boolean) =>
  optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/89887fa624480515334a3e6398f9e93cd839bcc56b3febbf6e08c2d46b144e51.png",
    },
    isSSR,
    { width: 448 }
  );

export const getLemuelMCImageUrl = (isSSR: boolean) =>
  optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/b330b586a357b6738c900a43f4a10179d2d549994685e335797fa4bd275b3173.png",
    },
    isSSR,
    { width: 448 }
  );

export const getPercivalNewBrandsImageUrl = (isSSR: boolean) =>
  optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/39a07e829872eef6d8b10bc5fa91f517c1af295bfd50a2cd6d3efba2349204d6.png",
    },
    isSSR,
    { width: 448 }
  );

export const getTinneMiaImageUrl = (isSSR: boolean) =>
  optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/6d23b98e8ff7be0196dc45fa97b9a6d2826e0b17a2a60816c6b2607da174fadf.png",
    },
    isSSR,
    { width: 448 }
  );
const brandNamePrefix = (
  <StrictLocalMsg
    defaultMessage="Featured brand:"
    description={{ text: "Featured brand" }}
  />
);

export const getImageSlider = (
  isApparelRetailer: boolean,
  sliderRef: Ref<Slider>,
  isSSR: boolean
) => {
  const tabithaEve2xImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/ad6a4612887313c0ac6002fed2b18b222eda1240b2df4153200018d0b4876ac4.png",
    },
    isSSR,
    { width: 896 }
  );

  const lemuelMC2xImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/4df84a163630c0903236b63d0ce0233cf4b3a1b7e604f1de54e822b6c2e09333.png",
    },
    isSSR,
    { width: 896 }
  );

  const percivalImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/ceb22f85c43e3d3c235037f35d131c4257d688172ae173241199f511d21463b4.png",
    },
    isSSR,
    { width: 448 }
  );

  const percival2xImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/a1d01f4f6a279a17d0e07eb3a0c032e22a911968c4713ff0aea7f2d4daf7f004.png",
    },
    isSSR,
    { width: 896 }
  );

  const ginaCusachsImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/362a7fae88587cee3e8ce79bd99a57acd2cf725c8f2688fe4285cc26d8ed957b.png",
    },
    isSSR,
    { width: 448 }
  );

  const ginaCusachs2xImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/02ed336c950f4340e3ccdf9f0af4613d2b4796e8811bfbb8a435badfcb285c88.png",
    },
    isSSR,
    { width: 896 }
  );

  const maniBodycareImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/3124cf071bbae1bd3d358077e69e9e4055a62c523459e600c3eaaf2a7933f687.png",
    },
    isSSR,
    { width: 448 }
  );

  const maniBodycare2xImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/51c641bf3efe0878d092173451cdc2c0d7b90a2285670669060c2d9676741f66.png",
    },
    isSSR,
    { width: 896 }
  );

  const tinneMia2xImageUrl = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/3e9c32e70b0fd7a060ff5d1375fc7fee646eecde00fe058f3ac1b6f6750a9b97.png",
    },
    isSSR,
    { width: 896 }
  );

  return (
    <StyledSlider
      adaptiveHeight={true}
      arrows={false}
      autoplay
      autoplaySpeed={5000}
      infinite
      pauseOnHover
      ref={sliderRef}
    >
      <Wrapper>
        <BannerImage
          $isApparelRetailer={isApparelRetailer}
          $image={getTabithaEveImageUrl(isSSR)}
          $image2x={tabithaEve2xImageUrl}
          $imageApparel={getLemuelMCImageUrl(isSSR)}
          $image2xApparel={lemuelMC2xImageUrl}
        />
        <BrandName>
          <BrandNameText>
            {brandNamePrefix}
            {NonBreaking.space}
            {isApparelRetailer ? "Lemuel MC" : "Tabitha Eve"}
          </BrandNameText>
        </BrandName>
      </Wrapper>
      <Wrapper>
        <BannerImage
          $isApparelRetailer={isApparelRetailer}
          $image={getTinneMiaImageUrl(isSSR)}
          $image2x={tinneMia2xImageUrl}
          $imageApparel={percivalImageUrl}
          $image2xApparel={percival2xImageUrl}
        />
        <BrandName>
          <BrandNameText>
            {brandNamePrefix}
            {NonBreaking.space}
            {isApparelRetailer ? "Percival" : "Tinne Mia"}
          </BrandNameText>
        </BrandName>
      </Wrapper>
      <Wrapper>
        <BannerImage
          $isApparelRetailer={isApparelRetailer}
          $image={maniBodycareImageUrl}
          $image2x={maniBodycare2xImageUrl}
          $imageApparel={ginaCusachsImageUrl}
          $image2xApparel={ginaCusachs2xImageUrl}
        />
        <BrandName>
          <BrandNameText>
            {brandNamePrefix}
            {NonBreaking.space}
            {isApparelRetailer ? "Gina Cusachs" : "Mani Bodycare"}
          </BrandNameText>
        </BrandName>
      </Wrapper>
    </StyledSlider>
  );
};

const StyledSlider = styled(Slider)`
  width: 100%;
  height: 172px;
  > div {
    height: 172px !important;
  }

  ${responsiveMediaQueries.mobile} {
    overflow: hidden;
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 50%;
    height: 320px;
    > div {
      height: 320px !important;
    }
  }

  .slick-track {
    height: 100%;
  }

  .slick-track div {
    height: 100%;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow-y: visible;
`;

const BrandName = styled.div`
  width: 100%;
  height: 20px !important;
  text-align: right;
  margin-top: ${getSpacing("1x")};
  position: absolute;
  left: 0;
`;

const BannerImage = styled.div<{
  $isApparelRetailer?: boolean;
  $image?: string;
  $image2x?: string;
  $imageApparel?: string;
  $image2xApparel?: string;
}>`
  flex-shrink: 0;
  width: 100%;
  height: 295px !important;
  background-image: url(${({ $isApparelRetailer, $image, $imageApparel }) =>
    $isApparelRetailer ? $imageApparel : $image});
  background-size: cover;
  background-position: center 30%;

  ${responsiveMediaQueries.tabletAndAbove} {
    background-image: url(${({
      $isApparelRetailer,
      $image2x,
      $image2xApparel,
    }) => ($isApparelRetailer ? $image2xApparel : $image2x)});
  }
`;

const BrandNameText = styled(Typography).attrs({
  variant: "paragraphSansRegular",
  color: Core.text.subdued,
})``;
